import { Alert, Button, Card, Divider, Form, Input, Modal, Space, Typography  } from 'antd';
import { LockOutlined, LoginOutlined, UserOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { actions } from '@/core/store';
import Cookies from '@/portal-cookie';
import PasswordResetForm from './passwordReset';
const { Text, Title } = Typography;


const passwordRules = [
	{ required: true, message: 'Password is required' },
	{
		min: 10,
		message: 'Password must be at least 10 characters',
		validateTrigger: 'onSubmit',
	},
];

const newPasswordRules = [
	{ required: true, message: 'New Password is required' },
	{
		min: 10,
		message: 'Password must be at least 10 characters',
		validateTrigger: 'onSubmit',
	},
	{
		pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?]).*$/,
		message: 'Password must include at least 1 uppercase letter, 1 number, and 1 special character.',
	},
];

const Login = () => {
	const dispatch = useDispatch();
	const [ form ] = Form.useForm();


	const handleLogin = useCallback((values) => {
		dispatch(actions.auth.handleLogin(values));
	}, [ dispatch ]);

	const handlePasswordChange = useCallback((values) => { 
		dispatch(actions.auth.resetPassword(values.username, values.password_reset2, values.code));
		form.resetFields()
	}, [ dispatch, form ]);

	const verifyToken = useCallback((token) => {
		dispatch(actions.auth.verifyToken(token));
	}, [ dispatch ]);

	const onPasswordReset = () => {
		dispatch(actions.auth.setState({loadSubmitEmailModal: true}));
	};

	const closePasswordReset = () => {
		dispatch(actions.auth.setState({loadSubmitEmailModal: false}));
	};


	const pending            = useSelector(state => state.auth.pending),
		  showPasswordChange = useSelector(state => state.auth.showPasswordChange),
		  showRelog          = useSelector(state => state.auth.showRelog),
		  verifyingToken     = useSelector(state => state.auth.verifyingToken),
		  errors             = useSelector(state => state.auth.errors),
		  submitEmailModal   = useSelector(state => state.auth.loadSubmitEmailModal);

	const authToken = Cookies.get('authToken');

	const cardTitle = useMemo(() => {
		if (showPasswordChange) {
			return 'Change Password';
		}

		return 'Log In';
	}, [showPasswordChange]);

	const submitText = useMemo(() => {
		if (showPasswordChange) {
			return 'Change Password';
		}

		return 'Log In';
	}, [showPasswordChange]);

	useEffect(() => {
		if(authToken && !showPasswordChange && !showRelog) {
			verifyToken(authToken);
		}
	}, [
		authToken,
		verifyToken,
		showPasswordChange,
		showRelog,
		//submitEmailModal,
	]);

	useEffect(() => {
		if(showRelog) {
			form.resetFields([ 'password' ]);
		}
	}, [ showRelog, form ]);

	if(verifyingToken) {
		return null;
	}


	return (
		<div 
			className="ts_home_content_container login_form"
			style={{
				minHeight: '50vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: 24,
			}}
		>

			<Card
				className="ts_content_container ts_portal_card"
				style={{
					width: '100%',
					maxWidth: 420,
					borderRadius: 12,
					boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
				}}
				bodyStyle={{
					padding: 28,
				}}
			>
				<Space
					direction="vertical"
					size={4}
					style={{
						width: '100%',
						textAlign: 'center',
						marginBottom: 24,
					}}
				>

					<LoginOutlined style={{ fontSize: 28 }} />
					<Title level={3} style={{ margin: 0 }}>
						{cardTitle}
					</Title>
					<Text type="secondary">
						{showPasswordChange
							? 'Set your new password to continue.'
							: 'Sign in to access the portal.'}
					</Text>

				</Space>
				

				{showPasswordChange &&
					<Alert
						className="login_alert"
						style={{ marginBottom: 16 }}
						type="warning"
						showIcon
						message="You must change your password before logging in. Please set your new password below along with the verification code that was sent to your email."
					/>
				}

				{showRelog &&
					<Alert
						className="login_alert"
						style={{ marginBottom: 16 }}
						type="success"
						showIcon
						message="Your password has been successfully changed. Please log in again with your new password."
					/>
				}

				{errors.map(message => (
					<Alert className="login_alert" message={message} key={message} type="error" style={{ marginBottom: 12 }} showIcon />)) 
				}

				<Form
					name="normal_login"
					className="login_form"
					layout="vertical"
					initialValues={{ remember: true }}
					onFinish={showPasswordChange
						? handlePasswordChange
						: handleLogin}
					form={form}
					requiredMark={false}
				>
					<Form.Item
						label="Email"
						name="username"
						rules={[ 
							{ required: true, message: 'Username is required' },
							{ type: 'email', message: 'Please enter a valid email address' },
						]}
					>
						<Input
							size="large"
							autoComplete={'email'}
							inputMode={'email'}
							type={'email'}
							name={'email'}
							prefix={<UserOutlined className="site_form_item_icon" />}
							placeholder={"Email"}
							disabled={pending || showRelog || showPasswordChange}
						/>
					</Form.Item>


					{!showPasswordChange &&
						<Form.Item
							name="password"
							label="Password"
							rules={passwordRules}
						>
							<Input.Password
								size="large"
								prefix={<LockOutlined className="site_form_item_icon" />}
								placeholder="Password"
								disabled={pending}
							/>
						</Form.Item>
					}

					{showPasswordChange && (
					<>
						<Form.Item
							name="code"
							label="Verification Code"
							rules={[ { required: true, message: 'Code is required' } ]}
						>
							<Input 
								size="large"
								prefix={<SafetyOutlined className="site_form_item_icon" />}
								className="ts_padding"
								disabled={pending} 
								placeholder="Verification Code"
							/>
						</Form.Item>
					


						<Form.Item
							label="New Password"
							name="password_reset1"
							rules={newPasswordRules}
						>
							<Input.Password
								prefix={<LockOutlined className="site_form_item_icon" />}
								type="password"
								size="large"
								autoComplete="new-password"
								placeholder="New Password"
								disabled={pending}
							/>
						</Form.Item>
					
					<Form.Item
						label="Confirm New Password"
						name="password_reset2"
						dependencies={['password_reset1']}
						rules={[
							{ required: true, message: 'New Password is required' },
							({ getFieldValue }) => ({
								validator(_, value) {
									if (!value || getFieldValue('password_reset1') === value) {
										return Promise.resolve();
									}
									return Promise.reject(new Error('Passwords do not match!'));
								},
							}),
						]}
					>
						<Input.Password
							size="large"
							prefix={<LockOutlined className="site-form-item-icon" />}
							type="password"
							placeholder="Re-enter New Password"
							disabled={pending}
						/>
					</Form.Item>
				</>
			)}
<Divider style={{ margin: '16px 0' }} />

					<Form.Item style={{ marginBottom: showPasswordChange ? 0 : 12 }}>
						<Button
							type="primary"
							htmlType="submit"
							className="ts_full_width ts_button_accent"
							size="large"
							loading={pending}
							block
						>
							{submitText}
						</Button>
					</Form.Item>
				</Form>

				{!showPasswordChange && (
					<Button
						block
						size="large"
						type="link"
						icon={<MailOutlined />}
						onClick={onPasswordReset}
						disabled={pending}
					>
						Reset Password
					</Button>
				)}

			</Card>
			{submitEmailModal && 
			<PasswordResetForm open={true} handleClose={closePasswordReset}/>
			}
			
		</div>

	);
};

export default Login;
